import { decodeBase64Label } from "./CommonUtils";

export const CHANGE_TYPE = Object.freeze({
  ADDED: "added",
  REMOVED: "removed",
  MODIFIED: "modified",
  UNCHANGED: "unchanged",
});

// Node keys that are never reported as field changes: identity/audit data, or handled structurally.
export const IGNORED_NODE_KEYS = new Set([
  "id",
  "elementId",
  "children",
  "hashCode",
  "createdBy",
  "createdDate",
  "lastUpdatedBy",
  "lastUpdatedDate",
]);

// Keys skipped at any depth; signed file urls change on every fetch.
export const IGNORED_NESTED_KEYS = new Set(["imageUrl", "fileUrl"]);

// Optional display label and normaliser per node key. Keys not listed here are
// still compared automatically and shown with a humanised key name.
export const NODE_FIELD_CONFIG = {
  label: { label: "Label", normalize: decodeBase64Label },
  helpImageText: { label: "Help text", normalize: decodeBase64Label },
  componentType: { label: "Type" },
  name: { label: "Field name" },
  buttonName: { label: "Element" },
  properties: { label: "Properties" },
  validations: { label: "Validations" },
  imagesData: { label: "Attachments" },
};

export const toPlainText = (value) =>
  typeof value === "string" ? value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : value;

const getNodeId = (node) => node?.elementId || node?.id || node?.name;

const humanizeKey = (key) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());

// Missing, null, "" and empty collections are treated as the same "no value".
const isEmptyValue = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0);

const deepEqual = (a, b) => {
  if (a === b || (isEmptyValue(a) && isEmptyValue(b))) {
    return true;
  }
  if (a == null || b == null || typeof a !== "object" || typeof b !== "object") {
    return a === b;
  }
  if (Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }
  const keys = new Set(
    [...Object.keys(a), ...Object.keys(b)].filter((key) => !IGNORED_NESTED_KEYS.has(key)),
  );
  return [...keys].every((key) => deepEqual(a[key], b[key]));
};

const normalizeField = (key, value) => {
  const normalize = NODE_FIELD_CONFIG[key]?.normalize;
  return normalize ? normalize(value) : value;
};

const computeFieldChanges = (prevNode, currNode) => {
  const keys = new Set([...Object.keys(prevNode || {}), ...Object.keys(currNode || {})]);
  const changes = [];
  keys.forEach((key) => {
    if (IGNORED_NODE_KEYS.has(key) || IGNORED_NESTED_KEYS.has(key)) {
      return;
    }
    const previous = normalizeField(key, prevNode?.[key]);
    const current = normalizeField(key, currNode?.[key]);
    if (!deepEqual(previous, current)) {
      changes.push({
        field: key,
        label: NODE_FIELD_CONFIG[key]?.label || humanizeKey(key),
        previous,
        current,
      });
    }
  });
  return changes;
};

const diffNode = (prevNode, currNode) => {
  const node = currNode || prevNode;
  const children = diffChildren(prevNode?.children, currNode?.children);

  let changeType;
  let fieldChanges = [];
  let directlyChanged;
  if (!prevNode) {
    changeType = CHANGE_TYPE.ADDED;
    directlyChanged = true;
  } else if (!currNode) {
    changeType = CHANGE_TYPE.REMOVED;
    directlyChanged = true;
  } else {
    fieldChanges = computeFieldChanges(prevNode, currNode);
    const hasChangedChild = children.some(
      (child) => child.changeType !== CHANGE_TYPE.UNCHANGED,
    );
    // A direct structural change means this node itself gained or lost a child.
    const hasDirectStructuralChange = children.some(
      (child) =>
        child.changeType === CHANGE_TYPE.ADDED ||
        child.changeType === CHANGE_TYPE.REMOVED,
    );
    changeType =
      fieldChanges.length > 0 || hasChangedChild
        ? CHANGE_TYPE.MODIFIED
        : CHANGE_TYPE.UNCHANGED;
    directlyChanged = fieldChanges.length > 0 || hasDirectStructuralChange;
  }

  return {
    id: getNodeId(node),
    componentType: node?.componentType,
    buttonName: node?.buttonName,
    name: node?.name,
    label: decodeBase64Label(node?.label),
    changeType,
    directlyChanged,
    fieldChanges,
    inPrevious: !!prevNode,
    inCurrent: !!currNode,
    previousLabel: prevNode ? decodeBase64Label(prevNode.label) : null,
    currentLabel: currNode ? decodeBase64Label(currNode.label) : null,
    children,
  };
};

const diffChildren = (prevChildren = [], currChildren = []) => {
  const prevList = prevChildren || [];
  const currList = currChildren || [];
  const result = [];

  // Match siblings by elementId, falling back to name, then position, so the
  // diff stays accurate even if element ids are regenerated across versions.
  const matchedPrev = new Set();
  const findMatch = (currChild, index) => {
    const byId = prevList.findIndex(
      (p, i) =>
        !matchedPrev.has(i) &&
        p?.elementId &&
        currChild?.elementId &&
        p.elementId === currChild.elementId,
    );
    if (byId !== -1) return byId;
    const byName = prevList.findIndex(
      (p, i) =>
        !matchedPrev.has(i) &&
        p?.name &&
        currChild?.name &&
        p.name === currChild.name &&
        p.componentType === currChild.componentType,
    );
    if (byName !== -1) return byName;
    const prevAtIndex = prevList[index];
    if (
      prevAtIndex &&
      !matchedPrev.has(index) &&
      prevAtIndex.componentType === currChild?.componentType &&
      !prevAtIndex.elementId &&
      !currChild?.elementId
    ) {
      return index;
    }
    return -1;
  };

  currList.forEach((currChild, index) => {
    const matchIndex = findMatch(currChild, index);
    if (matchIndex !== -1) {
      matchedPrev.add(matchIndex);
      result.push(diffNode(prevList[matchIndex], currChild));
    } else {
      result.push(diffNode(null, currChild));
    }
  });

  prevList.forEach((prevChild, index) => {
    if (!matchedPrev.has(index)) {
      result.push(diffNode(prevChild, null));
    }
  });

  return result;
};

const toKeywordKey = (keywords) =>
  Array.isArray(keywords) ? [...keywords].sort().join(", ") : "";

const toCategoryKey = (categories) =>
  Array.isArray(categories)
    ? categories
        .map((cat) => `${cat?.categoryType}: ${cat?.categoryName}`)
        .sort()
        .join(" | ")
    : "";

// Form-level keys to compare. The form root also carries version/audit fields, so this is an allow-list.
export const FORM_META_CONFIG = {
  title: { label: "Title" },
  description: { label: "Description" },
  keywords: { label: "Keywords", normalize: toKeywordKey },
  formCategories: { label: "Categories", normalize: toCategoryKey },
};

const computeFormMetaChanges = (previousForm, currentForm) =>
  Object.entries(FORM_META_CONFIG).reduce((changes, [field, config]) => {
    const normalize = config.normalize || ((value) => value);
    const previous = normalize(previousForm?.[field]);
    const current = normalize(currentForm?.[field]);
    if (!deepEqual(previous, current)) {
      changes.push({ field, label: config.label, previous, current });
    }
    return changes;
  }, []);

const tallySummary = (nodes, summary) => {
  nodes.forEach((node) => {
    // Count only real changes; a container modified solely by a deeper change
    // is treated as unchanged for the summary totals.
    const bucket =
      node.changeType === CHANGE_TYPE.MODIFIED && !node.directlyChanged
        ? CHANGE_TYPE.UNCHANGED
        : node.changeType;
    summary[bucket] = (summary[bucket] || 0) + 1;
    if (node.children && node.children.length) {
      tallySummary(node.children, summary);
    }
  });
};

export const diffForms = (previousForm, currentForm) => {
  const children = diffChildren(previousForm?.children, currentForm?.children);
  const summary = { added: 0, removed: 0, modified: 0, unchanged: 0 };
  tallySummary(children, summary);
  const metaChanges = computeFormMetaChanges(previousForm, currentForm);
  return {
    children,
    summary,
    metaChanges,
    hasChanges:
      summary.added > 0 ||
      summary.removed > 0 ||
      summary.modified > 0 ||
      metaChanges.length > 0,
  };
};
